import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Trash2, Pencil } from "lucide-react";
import Odontogram from "@/components/Odontogram";
import PatientFiles from "@/components/PatientFiles";
import TreatmentPlan from "@/components/TreatmentPlan";
import PerioChart from "@/components/PerioChart";
import ClinicalNotes from "@/components/ClinicalNotes";
import PatientTimeline from "@/components/PatientTimeline";
import PatientWhatsAppChat from "@/components/PatientWhatsAppChat";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";

const fmt = (iso) => iso ? new Date(iso).toLocaleString("sq-AL") : "—";

export default function PatientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { format } = useCurrency();
  const [patient, setPatient] = useState(null);
  const [treatments, setTreatments] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [financial, setFinancial] = useState(null);

  const load = async () => {
    const [p, t, a, i, f] = await Promise.all([
      api.get(`/patients/${id}`),
      api.get(`/treatments`, { params: { patient_id: id } }),
      api.get(`/appointments`, { params: { patient_id: id } }),
      api.get(`/invoices`, { params: { patient_id: id } }),
      api.get(`/financial/patient/${id}`),
    ]);
    setPatient(p.data);
    setTreatments(t.data);
    setAppointments(a.data);
    setInvoices(i.data);
    setFinancial(f.data?.summary || null);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const remove = async () => {
    if (!window.confirm("Jeni i sigurt që doni të fshini këtë pacient dhe të gjitha të dhënat?")) return;
    try {
      await api.delete(`/patients/${id}`);
      toast.success("Pacienti u fshi");
      nav("/pacientet");
    } catch (e) {
      toast.error("Nuk keni leje për këtë veprim");
    }
  };

  if (!patient) return <div className="text-sm text-[#5C5C5C]">Duke ngarkuar…</div>;

  return (
    <div className="space-y-8" data-testid="patient-detail">
      <Link to="/pacientet" className="inline-flex items-center gap-2 text-sm text-[#5C5C5C] hover:text-[#2C4C3B]">
        <ArrowLeft size={16} /> Kthehu te lista
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-center gap-5">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#EAE4D9] text-[#2C4C3B] text-xl font-semibold">
            {patient.first_name?.[0]}{patient.last_name?.[0]}
          </div>
          <div>
            <div className="label-eyebrow">Dosja Klinike</div>
            <h1 className="mt-1 text-4xl font-semibold tracking-tight">{patient.first_name} {patient.last_name}</h1>
            <div className="mt-1 text-sm text-[#5C5C5C]">
              {patient.phone || "—"} · {patient.email || "Pa email"} · {patient.date_of_birth || "Datëlindja n/a"}
            </div>
          </div>
        </div>
        {["admin","doctor","reception"].includes(user?.role) && (
          <div className="flex gap-2">
            <EditPatientButton patient={patient} onUpdated={load} />
            {["admin","doctor"].includes(user?.role) && (
              <button onClick={remove} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium text-[#9E3C3C] hover:bg-[#9E3C3C]/10" data-testid="delete-patient-button">
                <Trash2 size={16} /> Fshi
              </button>
            )}
          </div>
        )}
      </div>

      <Tabs defaultValue="profile">
        <TabsList className="bg-[#EAE4D9]">
          <TabsTrigger value="profile" data-testid="tab-profile">Profili</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline">Historiku</TabsTrigger>
          <TabsTrigger value="odontogram" data-testid="tab-odontogram">Odontograma</TabsTrigger>
          <TabsTrigger value="perio" data-testid="tab-perio">Perio</TabsTrigger>
          <TabsTrigger value="plan" data-testid="tab-plan">Plan Trajtimi</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">Shënime SOAP</TabsTrigger>
          <TabsTrigger value="files" data-testid="tab-files">Skedarë</TabsTrigger>
          <TabsTrigger value="treatments" data-testid="tab-treatments">Trajtimet</TabsTrigger>
          <TabsTrigger value="appointments" data-testid="tab-appointments">Takimet</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Faturat</TabsTrigger>
          <TabsTrigger value="whatsapp" data-testid="tab-whatsapp">WhatsApp</TabsTrigger>
        </TabsList>

        {/* Financial Summary banner — always visible (right under tabs) */}
        {financial && <FinancialBanner f={financial} format={format} /> }

        <TabsContent value="profile" className="mt-6">
          <div className="surface-card p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Info label="Adresa" value={patient.address} />
            <Info label="Gjinia" value={patient.gender} />
            <Info label="Historiku Mjekësor" value={patient.medical_history} wide />
            <Info label="Alergji" value={patient.allergies} wide />
            <Info label="Shënime" value={patient.notes} wide />
          </div>
          <RecallEditor key={patient.id + ':' + (patient.recall_interval_months || 6) + ':' + (patient.recall_enabled !== false)} patient={patient} onUpdate={load} />
        </TabsContent>

        <TabsContent value="timeline" className="mt-6">
          <PatientTimeline patientId={id} />
        </TabsContent>

        <TabsContent value="odontogram" className="mt-6">
          <Odontogram patientId={id} />
        </TabsContent>

        <TabsContent value="perio" className="mt-6">
          <PerioChart patientId={id} />
        </TabsContent>

        <TabsContent value="plan" className="mt-6">
          <TreatmentPlan patientId={id} />
        </TabsContent>

        <TabsContent value="notes" className="mt-6">
          <ClinicalNotes patientId={id} />
        </TabsContent>

        <TabsContent value="files" className="mt-6">
          <PatientFiles patientId={id} />
        </TabsContent>

        <TabsContent value="treatments" className="mt-6">
          <div className="surface-card overflow-hidden">
            {treatments.length === 0 ? (
              <div className="p-10 text-center text-sm text-[#5C5C5C]">Pa trajtime të regjistruara.</div>
            ) : (
              <ul className="divide-y divide-[#E2DFD8]">
                {treatments.map((t) => (
                  <li key={t.id} className="flex items-center justify-between px-6 py-4">
                    <div>
                      <div className="font-medium">{t.name} {t.tooth_number ? `· Dhëmbi ${t.tooth_number}` : ""}</div>
                      <div className="text-sm text-[#5C5C5C]">{t.description || "—"} · {fmt(t.performed_at)} · {t.performed_by}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm">{format(t.cost)}</div>
                      <div className="text-xs uppercase tracking-[0.15em] text-[#5C5C5C]">{t.status}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>

        <TabsContent value="appointments" className="mt-6">
          <div className="surface-card overflow-hidden">
            {appointments.length === 0 ? (
              <div className="p-10 text-center text-sm text-[#5C5C5C]">Pa takime.</div>
            ) : (
              <ul className="divide-y divide-[#E2DFD8]">
                {appointments.map((a) => (
                  <li key={a.id} className="flex items-center justify-between px-6 py-4">
                    <div>
                      <div className="font-medium">{a.reason || "Konsultë"}</div>
                      <div className="text-sm text-[#5C5C5C]">{fmt(a.start_time)} → {fmt(a.end_time)}</div>
                    </div>
                    <div className="text-xs uppercase tracking-[0.15em] text-[#5C5C5C]">{a.status}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>

        <TabsContent value="invoices" className="mt-6">
          <div className="surface-card overflow-hidden">
            {invoices.length === 0 ? (
              <div className="p-10 text-center text-sm text-[#5C5C5C]">Pa fatura.</div>
            ) : (
              <ul className="divide-y divide-[#E2DFD8]">
                {invoices.map((inv) => (
                  <li key={inv.id} className="flex items-center justify-between px-6 py-4">
                    <div>
                      <div className="font-medium">{inv.invoice_number}</div>
                      <div className="text-sm text-[#5C5C5C]">{fmt(inv.created_at)} · {inv.items?.length || 0} zëra</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm">{format(inv.total)}</div>
                      <div className="text-xs uppercase tracking-[0.15em] text-[#5C5C5C]">{inv.status}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>

        <TabsContent value="whatsapp" className="mt-6">
          <PatientWhatsAppChat patientId={id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Info({ label, value, wide }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <div className="label-eyebrow">{label}</div>
      <div className="mt-1 text-sm text-[#1A1A1A] whitespace-pre-wrap">{value || "—"}</div>
    </div>
  );
}


const FIN_STATUS = {
  no_charge:  { label: "Pa shërbim",   cls: "bg-[#EAE4D9] text-[#5C5C5C]" },
  uninvoiced: { label: "Pa faturuar",  cls: "bg-[#D4A24C]/15 text-[#8B6914]" },
  unpaid:     { label: "Pa paguar",    cls: "bg-[#9E3C3C]/15 text-[#9E3C3C]" },
  partial:    { label: "Pjesërisht",   cls: "bg-[#C9963A]/15 text-[#8B6914]" },
  paid_full:  { label: "Paguar plotë", cls: "bg-[#2C4C3B] text-[#FDFBF7]" },
  prepaid:    { label: "Parapaguar",   cls: "bg-[#4A8B71]/20 text-[#2C4C3B]" },
};

function FinancialBanner({ f, format }) {
  const meta = FIN_STATUS[f.status] || FIN_STATUS.no_charge;
  return (
    <div className="surface-card mt-6 p-5" data-testid="financial-banner">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <span className="label-eyebrow">Gjendja Financiare</span>
          <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${meta.cls}`} data-testid="financial-status">
            {meta.label}
          </span>
        </div>
        {f.balance > 0 && (
          <div className="text-sm text-[#9E3C3C]">
            <span className="opacity-70">Mbetet për të paguar:</span>{" "}
            <span className="font-mono font-semibold">{format(f.balance)}</span>
          </div>
        )}
        {f.balance < 0 && (
          <div className="text-sm text-[#2C4C3B]">
            <span className="opacity-70">Parapagim / Kredi:</span>{" "}
            <span className="font-mono font-semibold">{format(-f.balance)}</span>
          </div>
        )}
        {f.uninvoiced_work > 0 && (
          <div className="text-sm text-[#8B6914]">
            <span className="opacity-70">Punë pa faturuar:</span>{" "}
            <span className="font-mono font-semibold">{format(f.uninvoiced_work)}</span>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <FinStat label={`Trajtime (${f.treatments_count})`} value={format(f.treatments_total)} testId="fin-treatments" />
        <FinStat label={`Faturuar (${f.invoices_count})`}   value={format(f.invoiced_total)}   testId="fin-invoiced" />
        <FinStat label="Paguar"                              value={format(f.paid_total)}        color="text-[#2C4C3B]" testId="fin-paid" />
        <FinStat label={`Plan i ardhshëm (${f.plans_count})`} value={format(f.plans_total)}    color="text-[#5C5C5C]" testId="fin-plans" />
      </div>
    </div>
  );
}

function FinStat({ label, value, color = "text-[#1A1A1A]", testId }) {
  return (
    <div className="rounded-lg bg-[#FDFBF7] p-3 border border-[#E2DFD8]" data-testid={testId}>
      <div className="label-eyebrow">{label}</div>
      <div className={`mt-1 text-lg font-semibold font-mono tracking-tight ${color}`}>{value}</div>
    </div>
  );
}

function RecallEditor({ patient, onUpdate }) {
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(patient?.recall_enabled !== false);
  const [months, setMonths] = useState(patient?.recall_interval_months || 6);

  const nextDate = (() => {
    const last = patient?.last_recall_date || patient?.last_visit;
    if (!last || !enabled) return null;
    try {
      const d = new Date(last);
      d.setMonth(d.getMonth() + (months || 6));
      return d;
    } catch { return null; }
  })();

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/patients/${patient.id}`, {
        recall_interval_months: months,
        recall_enabled: enabled,
      });
      toast.success("Rikontrolli u ruajt");
      if (onUpdate) await onUpdate();
    } catch (e) {
      toast.error("Gabim në ruajtje");
    } finally { setSaving(false); }
  };

  if (!patient) return null;
  return (
    <div className="surface-card p-6 mt-4" data-testid="recall-editor">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Rikontroll Periodik</div>
          <p className="text-sm text-[#5C5C5C] mt-1">
            Cakto sa muaj pas vizitës së fundit të dërgohet kujtesa automatike te pacienti.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={enabled}
                 onChange={(e) => setEnabled(e.target.checked)}
                 data-testid="recall-detail-enabled" />
          {enabled ? "Aktiv" : "Joaktiv"}
        </label>
      </div>

      <div className="mt-4 grid grid-cols-5 sm:grid-cols-6 gap-2">
        {[3, 6, 9, 12, 18, 24].map((m) => (
          <button key={m} type="button"
                  onClick={() => setMonths(m)}
                  disabled={!enabled}
                  data-testid={`recall-detail-preset-${m}`}
                  className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                    months === m ? "border-[#2C4C3B] bg-[#2C4C3B] text-white"
                                 : "border-[#E2DFD8] bg-white text-[#5C5C5C] hover:border-[#2C4C3B] hover:text-[#2C4C3B]"
                  } ${!enabled ? "opacity-40 cursor-not-allowed" : ""}`}>
            {m} muaj
          </button>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <label className="text-xs text-[#5C5C5C]">Ose personalizo:</label>
        <input type="number" min="1" max="60" value={months}
               onChange={(e) => setMonths(Number(e.target.value) || 6)}
               disabled={!enabled}
               data-testid="recall-detail-months"
               className="w-24 rounded-md border border-[#E2DFD8] px-3 py-1.5 text-sm" />
        <span className="text-xs text-[#5C5C5C]">muaj</span>
      </div>

      {nextDate && (
        <div className="mt-4 rounded-lg bg-[#EAE4D9]/50 border border-[#C9C2B2] p-3 flex items-center justify-between">
          <div>
            <div className="text-xs text-[#5C5C5C]">Rikontrolli i ardhshëm i parashikuar:</div>
            <div className="text-base font-semibold text-[#2C4C3B]">
              {nextDate.toLocaleDateString("sq-AL", { dateStyle: "full" })}
            </div>
            <div className="text-[11px] text-[#5C5C5C] mt-0.5">
              Kujtesa do dërgohet automatikisht 7 ditë para datës
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 flex justify-end">
        <button onClick={save} disabled={saving}
                className="btn-primary"
                data-testid="recall-save-button">
          {saving ? "Duke ruajtur..." : "Ruaj"}
        </button>
      </div>
    </div>
  );
}



// ─────────────────────────────────────────────────────────────────────────
// Edit Patient — modal qe parambushet me te dhenat aktuale dhe ben PUT
// ─────────────────────────────────────────────────────────────────────────
function EditPatientButton({ patient, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(() => ({
    first_name: patient.first_name || "",
    last_name: patient.last_name || "",
    phone: patient.phone || "",
    email: patient.email || "",
    date_of_birth: patient.date_of_birth || "",
    gender: patient.gender || "tjeter",
    address: patient.address || "",
    medical_history: patient.medical_history || "",
    allergies: patient.allergies || "",
    notes: patient.notes || "",
  }));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      first_name: patient.first_name || "",
      last_name: patient.last_name || "",
      phone: patient.phone || "",
      email: patient.email || "",
      date_of_birth: patient.date_of_birth || "",
      gender: patient.gender || "tjeter",
      address: patient.address || "",
      medical_history: patient.medical_history || "",
      allergies: patient.allergies || "",
      notes: patient.notes || "",
    });
  }, [patient.id, patient.first_name, patient.last_name, patient.phone, patient.email, patient.date_of_birth, patient.gender, patient.address, patient.medical_history, patient.allergies, patient.notes]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/patients/${patient.id}`, form);
      toast.success("Pacienti u përditësua");
      setOpen(false);
      onUpdated && onUpdated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gabim gjatë përditësimit");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium text-[#2C4C3B] hover:bg-[#2C4C3B]/10"
        data-testid="edit-patient-button"
      >
        <Pencil size={16} /> Edito
      </button>
      <DialogContent className="bg-white max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edito Pacientin</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="edit-patient-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow">Emri</Label>
              <Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-2" data-testid="edit-first-name" />
            </div>
            <div>
              <Label className="label-eyebrow">Mbiemri</Label>
              <Input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-2" data-testid="edit-last-name" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow">Telefoni</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2" data-testid="edit-phone" />
            </div>
            <div>
              <Label className="label-eyebrow">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2" data-testid="edit-email" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow">Datëlindja</Label>
              <Input type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} className="mt-2" data-testid="edit-dob" />
            </div>
            <div>
              <Label className="label-eyebrow">Gjinia</Label>
              <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                <SelectTrigger className="mt-2" data-testid="edit-gender"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mashkull">Mashkull</SelectItem>
                  <SelectItem value="femer">Femër</SelectItem>
                  <SelectItem value="tjeter">Tjetër</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="label-eyebrow">Adresa</Label>
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-2" data-testid="edit-address" />
          </div>
          <div>
            <Label className="label-eyebrow">Historiku Mjekësor</Label>
            <Textarea rows={2} value={form.medical_history} onChange={(e) => setForm({ ...form, medical_history: e.target.value })} className="mt-2" data-testid="edit-history" />
          </div>
          <div>
            <Label className="label-eyebrow">Alergji</Label>
            <Input value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })} className="mt-2" data-testid="edit-allergies" />
          </div>
          <div>
            <Label className="label-eyebrow">Shënime</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2" data-testid="edit-notes" />
          </div>
          <DialogFooter>
            <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>Anulo</button>
            <button type="submit" className="btn-primary" disabled={saving} data-testid="edit-patient-save">
              {saving ? "Duke ruajtur..." : "Ruaj"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
